import { Stack, Button, useTheme, DialogActions, Tooltip } from '@mui/material';
import { Dispatch, SetStateAction } from 'react';
import {
  COMMON_STRING,
  DELETE_CONFIRMATION_STRING,
} from '@/constants/dictionary';
import { API_METHOD } from '@/service/constants';
import { ConfirmationButtonSet } from '../common/ConfirmationButtonSet';
import { EvaluationTypes } from './constants';
import { compareEvaluations } from './utils/utils';

interface SupervisorEvaluationButtonsProps {
  newEvaluation: EvaluationTypes;
  onSubmit: (
    action: API_METHOD.POST | API_METHOD.PUT | API_METHOD.DELETE,
  ) => Promise<void>;
  setEvaluation: Dispatch<SetStateAction<EvaluationTypes>>;
  initialEvaluation: EvaluationTypes;
  isOpenDeleteConfirmation: boolean;
  setIsOpenDeleteConfirmation: Dispatch<React.SetStateAction<boolean>>;
}

export const SupervisorEvaluationButtons = ({
  newEvaluation,
  onSubmit,
  setEvaluation,
  initialEvaluation,
  isOpenDeleteConfirmation,
  setIsOpenDeleteConfirmation,
}: SupervisorEvaluationButtonsProps) => {
  const theme = useTheme();

  const hasChanges = compareEvaluations(newEvaluation, initialEvaluation);

  return (
    <DialogActions
      sx={{ p: theme.spacing(2, 3), justifyContent: 'space-between' }}
    >
      <Tooltip
        placement='top'
        open={isOpenDeleteConfirmation}
        disableHoverListener
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[1],
            },
          },
        }}
        title={
          <ConfirmationButtonSet
            onConfirm={() => onSubmit(API_METHOD.DELETE)}
            onClose={() => setIsOpenDeleteConfirmation(false)}
            confirmMessage={DELETE_CONFIRMATION_STRING.SUPERVISOR_EVALUATION}
          />
        }
      >
        <Button
          variant='text'
          disabled={
            (!initialEvaluation.feedbackType &&
              !initialEvaluation.feedbackText) ||
            isOpenDeleteConfirmation
          }
          onClick={() => setIsOpenDeleteConfirmation(true)}
        >
          {COMMON_STRING.REMOVE}
        </Button>
      </Tooltip>
      <Stack flexDirection='row' gap={2}>
        <Button
          variant='contained'
          disabled={hasChanges || !newEvaluation.feedbackType}
          onClick={() =>
            !!(newEvaluation.feedbackType && !initialEvaluation.feedbackType)
              ? onSubmit(API_METHOD.POST)
              : !!(newEvaluation.feedbackType && initialEvaluation.feedbackType)
              ? onSubmit(API_METHOD.PUT)
              : null
          }
        >
          {!!initialEvaluation.feedbackType
            ? COMMON_STRING.SAVE
            : COMMON_STRING.ADD}
        </Button>
        <Button
          variant='outlined'
          disabled={hasChanges || !newEvaluation.feedbackType}
          onClick={() => setEvaluation(initialEvaluation)}
        >
          {COMMON_STRING.CANCEL}
        </Button>
      </Stack>
    </DialogActions>
  );
};
